import React, { useEffect, useState } from 'react';
import { TelegramChat, TelegramMessage } from '../listing/TelegramPreview';
import { DEMO_LISTINGS } from '../listing/demoListing';
import { cn } from '../../utils/cn';
import { QuickStart } from './QuickStart';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

/**
 * The hero: the product's one bold element ("die Meldung") as a Telegram chat in a
 * drawn phone silhouette next to the real PLZ entry (`QuickStart`). Telegram is the
 * channel we recommend, so it is the only one shown. The two demo messages slide in
 * once on first paint (400ms, 1.2s apart); under
 * `prefers-reduced-motion` both render in their final state immediately.
 */
export interface HeroProps {
  /** Forwarded to `QuickStart` — see `QuickStartProps.onStepChange` (M8). */
  onQuickStartStepChange?: (step: 1 | 2) => void;
}

export const Hero: React.FC<HeroProps> = ({ onQuickStartStepChange }) => {
  const [visible, setVisible] = useState<[boolean, boolean]>(() =>
    prefersReducedMotion() ? [true, true] : [false, false]
  );

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const showFirst = window.setTimeout(() => setVisible(([, second]) => [true, second]), 60);
    const showSecond = window.setTimeout(() => setVisible(([first]) => [first, true]), 1200);
    return () => {
      window.clearTimeout(showFirst);
      window.clearTimeout(showSecond);
    };
  }, []);

  return (
    <section id="start" className="scroll-mt-20 bg-page py-12 md:py-20">
      <div className="mx-auto grid w-full max-w-wide gap-10 px-4 md:px-6 lg:grid-cols-2 lg:items-center lg:gap-16">
        <div>
          <h1 className="hero-title font-display text-2xl text-ink sm:text-3xl lg:text-4xl">
            Neue Wohnungen aus 20+ Portalen, Minuten nach Veröffentlichung auf deinem Handy.
          </h1>
          <p className="hero-subtitle mt-4 max-w-narrow text-base text-muted">
            Du gibst deine PLZ ein, wir beobachten die Portale und schicken dir jede passende Wohnung als
            Benachrichtigung über Telegram, Push oder E-Mail.
          </p>

          <div className="mt-8">
            <QuickStart onStepChange={onQuickStartStepChange} />
          </div>

          <p className="hero-stats mt-6 text-sm text-muted">
            Wir beobachten <span className="stat-number tabular font-semibold text-ink">20+</span> Portale rund um
            die Uhr.
          </p>
        </div>

        <div>
          <div className="mx-auto w-[88%] max-w-[330px] lg:w-full">
            {/* Theme-invariant scrim bezel in light mode; in dark mode the scrim equals the page
                background, so the bezel lifts to line-strong to stay visible (M7). */}
            <div className="rounded-[2.5rem] border-[6px] border-scrim bg-scrim p-1.5 shadow-card dark:border-line-strong/70 dark:bg-card">
              <div className="relative flex h-[560px] flex-col overflow-hidden rounded-[2rem]">
                <p className="sr-only">Beispiel einer Meldung, so wie sie bei dir in Telegram ankommt.</p>
                <TelegramChat>
                  {DEMO_LISTINGS.map((listing, i) => (
                    <div
                      key={listing.title}
                      className={cn(
                        'transition-all duration-[400ms] ease-out',
                        visible[i] ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0'
                      )}
                    >
                      <TelegramMessage listing={listing} />
                    </div>
                  ))}
                </TelegramChat>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
